import { useCallback, useEffect, useRef, useState } from 'react'
import {
  ActionIcon,
  Badge,
  Box,
  Group,
  Loader,
  Progress,
  ScrollArea,
  Stack,
  Text,
  Tooltip,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconArrowsMaximize, IconArrowsMinimize, IconCopy, IconDownload, IconX } from '@tabler/icons-react'
import { api } from '@/api/client'
import type { FileItem, FileReadResponse } from './types'
import { LANGUAGE_COLORS, formatFileSize } from './types'

const MAX_RENDER_LINES = 1000
const MIN_HEIGHT = 120
const DEFAULT_HEIGHT = 280

interface FilePreviewProps {
  tenantId: number
  instanceId: string
  file: FileItem | null
  filePath: string
  onClose: () => void
  onBlocked?: (fileName: string) => void
  expanded?: boolean
  onToggleExpand?: () => void
}

export function FilePreview({ tenantId, instanceId, file, filePath, onClose, onBlocked, expanded, onToggleExpand }: FilePreviewProps) {
  const [displayContent, setDisplayContent] = useState('')
  const [loading, setLoading] = useState(false)
  const [s3Loading, setS3Loading] = useState(false)
  const [language, setLanguage] = useState('Text')
  const [height, setHeight] = useState(DEFAULT_HEIGHT)
  const heightRef = useRef(DEFAULT_HEIGHT)
  const dragging = useRef(false)

  useEffect(() => {
    if (!file) {
      setDisplayContent('')
      setLoading(false)
      setS3Loading(false)
      setLanguage('Text')
      return
    }

    let cancelled = false

    async function fetchContent() {
      setLoading(true)
      setS3Loading(false)
      setDisplayContent('')

      try {
        const { data } = await api.get<FileReadResponse>(
          `/v1/tenants/${tenantId}/inventory/ec2/${instanceId}/files/read`,
          { params: { path: filePath } },
        )

        if (cancelled) return

        setLanguage(data.language || 'Text')

        if (data.content != null) {
          setDisplayContent(data.content)
          setLoading(false)
        } else if (data.presigned_url) {
          setLoading(false)
          setS3Loading(true)

          const res = await fetch(data.presigned_url)
          if (cancelled) return

          const text = await res.text()
          if (cancelled) return

          setDisplayContent(text)
          setS3Loading(false)
        } else {
          setDisplayContent('')
          setLoading(false)
        }
      } catch (err: unknown) {
        if (cancelled) return
        const status = (err as { response?: { status?: number } })?.response?.status
        if (status === 403) {
          notifications.show({
            color: 'orange',
            title: 'Arquivo bloqueado',
            message: 'Sem permissão de leitura para este arquivo.',
          })
          onBlocked?.(file!.name)
          onClose()
        } else {
          notifications.show({
            color: 'red',
            title: 'Erro ao ler arquivo',
            message: 'Não foi possível carregar o conteúdo do arquivo.',
          })
        }
        setDisplayContent('')
        setLoading(false)
        setS3Loading(false)
      }
    }

    fetchContent()

    return () => {
      cancelled = true
    }
  }, [file, filePath, tenantId, instanceId])

  const onDragStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    dragging.current = true
    const startY = e.clientY
    const startH = heightRef.current

    const onMove = (ev: MouseEvent) => {
      const delta = startY - ev.clientY
      const newH = Math.max(MIN_HEIGHT, startH + delta)
      heightRef.current = newH
      setHeight(newH)
    }
    const onUp = () => {
      dragging.current = false
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
    }
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
  }, [])

  if (!file) return null

  const lines = displayContent.split('\n')
  const truncated = lines.length > MAX_RENDER_LINES
  const visibleLines = truncated ? lines.slice(0, MAX_RENDER_LINES) : lines

  function handleCopy() {
    navigator.clipboard.writeText(displayContent).then(() => {
      notifications.show({ color: 'green', message: 'Copiado!' })
    })
  }

  function handleDownload() {
    const blob = new Blob([displayContent], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = file!.name
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const contentHeight = expanded ? '100%' : height

  return (
    <Box style={{
      borderTop: '1px solid var(--mantine-color-dark-4)',
      display: 'flex',
      flexDirection: 'column',
      height: expanded ? '100%' : undefined,
    }}>
      {/* Drag handle for resize (only when not expanded) */}
      {!expanded && (
        <Box
          onMouseDown={onDragStart}
          style={{
            height: 4,
            cursor: 'row-resize',
            background: 'transparent',
            flexShrink: 0,
          }}
          onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = 'var(--mantine-color-blue-8)' }}
          onMouseLeave={(e) => { if (!dragging.current) (e.currentTarget as HTMLElement).style.background = 'transparent' }}
        />
      )}

      {/* Header */}
      <Group justify="space-between" px="md" py="xs" style={{ borderBottom: '1px solid var(--mantine-color-dark-4)', flexShrink: 0 }}>
        <Group gap="sm">
          <Text fw={600} size="sm">{file.name}</Text>
          <Text size="xs" c="dimmed">
            {formatFileSize(file.size)} &middot; {file.permissions}
          </Text>
        </Group>

        <Badge color={LANGUAGE_COLORS[language] || 'gray'} size="sm" variant="light">
          {language}
        </Badge>

        <Group gap={4}>
          <ActionIcon
            variant="subtle"
            size="sm"
            color="gray"
            onClick={handleCopy}
            disabled={loading || s3Loading || !displayContent}
            title="Copiar"
          >
            <IconCopy size={14} />
          </ActionIcon>
          <ActionIcon
            variant="subtle"
            size="sm"
            color="gray"
            onClick={handleDownload}
            disabled={loading || s3Loading || !displayContent}
            title="Download"
          >
            <IconDownload size={14} />
          </ActionIcon>
          {onToggleExpand && (
            <Tooltip label={expanded ? 'Reduzir' : 'Expandir'}>
              <ActionIcon variant="subtle" size="sm" color="gray" onClick={onToggleExpand}>
                {expanded ? <IconArrowsMinimize size={14} /> : <IconArrowsMaximize size={14} />}
              </ActionIcon>
            </Tooltip>
          )}
          <ActionIcon variant="subtle" size="sm" color="gray" onClick={onClose} title="Fechar">
            <IconX size={14} />
          </ActionIcon>
        </Group>
      </Group>

      {/* Content */}
      <ScrollArea style={{ flex: expanded ? 1 : undefined, height: expanded ? undefined : contentHeight, background: 'var(--mantine-color-dark-8)' }}>
        {loading && (
          <Stack align="center" justify="center" h={expanded ? '100%' : contentHeight} mih={200}>
            <Loader size="sm" />
          </Stack>
        )}

        {s3Loading && (
          <Stack align="center" justify="center" h={expanded ? '100%' : contentHeight} mih={200} px="xl">
            <Progress value={100} animated w="100%" />
            <Text size="xs" c="dimmed">Carregando arquivo grande via S3...</Text>
          </Stack>
        )}

        {!loading && !s3Loading && (
          <Box p="xs" style={{ fontFamily: 'monospace', fontSize: 11, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
            {visibleLines.map((line, i) => (
              <div key={i} style={{ display: 'flex', gap: 12 }}>
                <Text span c="dimmed" style={{ userSelect: 'none', minWidth: 32, textAlign: 'right' }}>
                  {i + 1}
                </Text>
                <Text span c="gray.3">{line || ' '}</Text>
              </div>
            ))}
            {truncated && (
              <Text size="xs" c="yellow" ta="center" mt="sm">
                Exibindo as primeiras {MAX_RENDER_LINES} linhas de {lines.length}. Arquivo grande demais para renderizar por completo.
              </Text>
            )}
          </Box>
        )}
      </ScrollArea>
    </Box>
  )
}
